import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EvidenceBadge from "@/components/EvidenceBadge";
import { ArrowLeft, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";

const STATUS_MAP = {
    success: { cls: "bg-[var(--sage)] text-white border-[var(--sage)]", Icon: CheckCircle, label: "OK" },
    failed: { cls: "bg-[var(--terra-2)] text-white border-[var(--terra-2)]", Icon: XCircle, label: "Error" },
    running: { cls: "bg-[var(--amber)] text-white border-[var(--amber)]", Icon: Clock, label: "Ejecutando" },
};

export default function CorpusEventDetail() {
    const { id } = useParams();
    const [ev, setEv] = useState(null);

    useEffect(() => {
        api.get(`/corpus/events/${id}`).then((r) => setEv(r.data)).catch(() => setEv(null));
    }, [id]);

    if (!ev) return <div className="p-12"><span className="label-eyebrow">cargando…</span></div>;

    // Group new_evidence by compound
    const byCompound = (ev.new_evidence || []).reduce((acc, x) => {
        const k = x.compound_slug || "?";
        (acc[k] = acc[k] || { name: x.compound_name || k, refs: [] }).refs.push(x.ref);
        return acc;
    }, {});
    const groups = Object.entries(byCompound).sort((a, b) => b[1].refs.length - a[1].refs.length);

    const st = STATUS_MAP[ev.status] || STATUS_MAP.running;
    const SI = st.Icon;

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow={`Revisión bibliográfica · ${ev.triggered_by}`}
                title={`Ingesta del ${new Date(ev.started_at).toLocaleString("es-ES", { dateStyle: "long", timeStyle: "short" })}`}
                lead={`${ev.compounds_processed || 0} compuestos procesados · +${ev.new_evidence_count || 0} nuevas referencias indexadas`}
                actions={<span className={`citation-badge ${st.cls}`} data-testid="event-status"><SI size={12}/> {st.label}</span>}
            >
                <div className="mt-4">
                    <Link to="/corpus" className="text-sm text-[var(--ink-2)] inline-flex items-center gap-1 hover:text-[var(--ink)]">
                        <ArrowLeft size={14}/> Volver al log
                    </Link>
                </div>
            </PageHeader>

            <div className="px-8 md:px-12 py-8 max-w-6xl">
                {groups.length === 0 ? (
                    <div className="border hairline bg-white p-12 text-center text-[var(--ink-3)]" data-testid="event-no-new-evidence">
                        Esta ingesta no añadió referencias nuevas (los compuestos ya estaban actualizados).
                    </div>
                ) : (
                    <div className="space-y-6" data-testid="event-evidence-list">
                        {groups.map(([slug, grp]) => (
                            <section key={slug} className="border hairline bg-white" data-testid={`event-compound-${slug}`}>
                                <div className="flex items-center justify-between p-5 border-b hairline">
                                    <Link to={`/compuestos/${slug}`} className="font-display text-xl tracking-tight hover:text-[var(--sage)]">{grp.name}</Link>
                                    <span className="text-xs mono text-[var(--ink-3)]">+{grp.refs.length} ref{grp.refs.length === 1 ? "" : "s"}.</span>
                                </div>
                                <ul className="divide-y divide-[var(--line)]">
                                    {grp.refs.map((r, i) => (
                                        <li key={i} className="p-5" data-testid={`event-ref-${r.external_id || i}`}>
                                            <div className="flex items-start justify-between gap-3 mb-2">
                                                <a href={r.url} target="_blank" rel="noreferrer" className="font-medium text-[var(--ink)] hover:text-[var(--sage)] leading-snug">{r.title}</a>
                                                <EvidenceBadge source={r.source} externalId={r.external_id} url={r.url}/>
                                            </div>
                                            <div className="text-xs text-[var(--ink-2)] mono mb-2">
                                                {r.journal} {r.year ? `· ${r.year}` : ""}
                                            </div>
                                            {r.abstract && (
                                                <p className="text-sm text-[var(--ink-2)] leading-relaxed line-clamp-4">{r.abstract}</p>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ))}
                    </div>
                )}

                {(ev.errors || []).length > 0 && (
                    <section className="mt-10 border hairline bg-[var(--cream)] p-5" data-testid="event-errors">
                        <div className="label-eyebrow mb-3 text-[var(--terra-2)]">Errores durante la ingesta</div>
                        <ul className="text-xs space-y-1 mono text-[var(--ink-2)]">
                            {ev.errors.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                    </section>
                )}
            </div>
        </div>
    );
}
